/**
 * Doc-upload API client — Lane 616 (Phase 5, the front door).
 *
 * Thin wrapper over `POST /api/chat/upload/evaluate`, the endpoint that has
 * been live and wired since Commit ε and had ZERO frontend callers until this
 * lane (execution plan §3 break #1, re-verified with a working control search).
 *
 * THREE THINGS THIS FILE HAS TO GET RIGHT
 * ---------------------------------------
 * 1. `Content-Type` is NEVER set by hand. `fetch` derives
 *    `multipart/form-data; boundary=…` from the FormData body, and a hand-set
 *    header omits the boundary, which makes the server's multipart parse fail.
 *    `authFetch` spreads `options.headers` and adds only Authorization, so
 *    passing no headers here is sufficient and correct.
 *
 * 2. `file` is the only REQUIRED form field. The route mounts
 *    `require_form_fields({"file", "conversation_id"})`, which rejects any
 *    unexpected field name AND any duplicate part with HTTP 422 (Arc 5, CT-1
 *    defense in depth). `company_id` / `user_id` come from the JWT (Rule 106)
 *    — sending them would be both wrong and a 422. Lane 1400 WS1 adds the
 *    optional `conversation_id`, sent only while CHAT_UPLOAD_HONESTY_ENABLED
 *    is on: a lookup key inside the buyer's own company and user, never scope.
 *
 * 3. `retry` is a QUERY parameter, not a form field. Sending it as form-data
 *    would itself trip the allow-list above.
 */

import { API_BASE } from '../config';

/** Mirror of `helpers/chat_conversation_files.MAX_CONVERSATION_ID_CHARS`. */
export const MAX_CONVERSATION_ID_CHARS = 200;

/**
 * Upload one file for evaluation.
 *
 * Resolves to `{ ok, httpStatus, body }`:
 *   - `ok: true`  → a 2xx; `body` carries the pipeline `status`.
 *   - `ok: false` → a non-2xx; `body.detail` is the server's message.
 * Rejects only on a genuine transport failure (or on the 401 `authFetch`
 * turns into a forced logout), so the caller can tell "the server said no"
 * apart from "the upload never arrived".
 *
 * @param {(url: string, options?: object) => Promise<Response>} authFetch
 * @param {File}    file
 * @param {boolean} retry  bypass the 30-day dedup short-circuit (?retry=true)
 * @param {string|null} conversationId  Lane 1400 WS1: the chat conversation
 *   the file was attached in. Omitted (null) = the request is byte-identical
 *   to before the lane.
 */
export async function uploadDocumentForEvaluation(authFetch, file, retry = false,
  conversationId = null) {
  const form = new FormData();
  form.append('file', file);
  if (typeof conversationId === 'string' && conversationId) {
    form.append('conversation_id', conversationId.slice(0, MAX_CONVERSATION_ID_CHARS));
  }

  const url =
    `${API_BASE}/api/chat/upload/evaluate` + (retry ? '?retry=true' : '');

  // No `headers` — see note 1 above.
  const response = await authFetch(url, { method: 'POST', body: form });

  let body = null;
  try {
    body = await response.json();
  } catch {
    // A non-JSON body (proxy error page, empty 502) must not throw here — the
    // caller maps a missing body through describeHttpError, which is honest
    // about not knowing rather than inventing a reason.
    body = null;
  }

  return { ok: response.ok, httpStatus: response.status, body };
}
